import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { createSubCategory, getAllCategories, updateSubCategory } from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';

export default function SubCategoryFormDialog({
  open,
  subCategory,
  categoriaIdInicial = '',
  onClose,
  onSaved,
}) {
  const isEdit = !!subCategory?.id;
  const [descricao, setDescricao] = useState('');
  const [categoriaId, setCategoriaId] = useState('');
  const [categorias, setCategorias] = useState([]);
  const [loadingCategorias, setLoadingCategorias] = useState(false);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setDescricao(subCategory?.descricao || '');
    setCategoriaId(
      subCategory?.categoriaId
        ? String(subCategory.categoriaId)
        : (categoriaIdInicial ? String(categoriaIdInicial) : ''),
    );
    setTouched(false);
    setSaving(false);
    setError('');
  }, [open, subCategory, categoriaIdInicial]);

  useEffect(() => {
    if (!open) return;
    let cancelado = false;
    (async () => {
      setLoadingCategorias(true);
      try {
        const { data } = await getAllCategories({ indice: 1, qtde: 200 });
        if (!cancelado) setCategorias(data.items ?? []);
      } catch (err) {
        if (!cancelado) setError(getApiErrorMessage(err, 'Erro ao carregar categorias.'));
      } finally {
        if (!cancelado) setLoadingCategorias(false);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [open]);

  const descricaoInvalida = !descricao.trim();
  const categoriaInvalida = !categoriaId;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (descricaoInvalida || categoriaInvalida) return;

    setSaving(true);
    setError('');
    try {
      const payload = { descricao: descricao.trim(), categoriaId: Number(categoriaId) };
      if (isEdit) {
        await updateSubCategory(subCategory.id, { id: subCategory.id, ...payload });
        onSaved?.('Subcategoria atualizada', { id: subCategory.id, ...payload });
      } else {
        const { data } = await createSubCategory(payload);
        onSaved?.('Subcategoria criada', data);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao salvar a subcategoria.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ component: 'form', onSubmit: handleSubmit }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={800} noWrap>
            {isEdit ? 'Editar subcategoria' : 'Nova subcategoria'}
          </Typography>
          {isEdit && (
            <Typography variant="caption" color="text.secondary">
              Código {subCategory.id}
            </Typography>
          )}
        </Stack>
        <IconButton onClick={onClose} disabled={saving} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Descrição"
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            onBlur={() => setTouched(true)}
            error={touched && descricaoInvalida}
            helperText={touched && descricaoInvalida ? 'Informe a descrição.' : ' '}
            autoFocus
            fullWidth
            inputProps={{ maxLength: 100 }}
          />
          <TextField
            select
            label="Categoria"
            value={categoriaId}
            onChange={(e) => setCategoriaId(e.target.value)}
            onBlur={() => setTouched(true)}
            error={touched && categoriaInvalida}
            helperText={
              touched && categoriaInvalida
                ? 'Selecione a categoria.'
                : loadingCategorias
                  ? 'Carregando categorias...'
                  : ' '
            }
            disabled={loadingCategorias}
            fullWidth
          >
            {categorias.map((categoria) => (
              <MenuItem key={categoria.id} value={String(categoria.id)}>
                {categoria.descricao}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isEdit ? 'Salvar' : 'Criar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
